import { useCallback, type MouseEvent } from 'react'
import TimeAgo from 'react-timeago'
import type { CalendarEvent } from '@highlight/common'
import { Button } from '@highlight/ui'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { useSetSidepanelHotkeyOpened } from '@renderer/overlay/Sidepanel/atoms'
import { MEETING_VIEW_ID } from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'
import { useViewActions } from '@renderer/overlay/Sidepanel/view-atoms'
import { trackSidepanelOpened } from '@renderer/overlay/utils/track-sidepanel'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { NotchToastIcon } from '../../notch-toast-icon'
import { useToast } from '../../use-toast'
import { meetingCountdownFormatter } from './utils'
import { useMeetingStartingSoonToast } from './use-meeting-starting-soon-toast'
import { twMerge } from 'tailwind-merge'

const NOTCH_MEETING_BUTTON_CLASS = 'h-8 w-full rounded-lg px-3 text-sm'

interface NotchMeetingStartingSoonToastProps {
  meeting: CalendarEvent
  toastId: string
  force?: boolean
}

export function NotchMeetingStartingSoonToast({ meeting, toastId, force = false }: NotchMeetingStartingSoonToastProps) {
  const { meetingJoinLink, joinLabel, transcribeLabel, transcribesOnJoin, onJoin, onStartTranscribing } =
    useMeetingStartingSoonToast({
      meeting,
      toastId,
      force,
    })

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2.5">
        <NotchToastIcon className="mt-0.5" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="line-clamp-2 break-words text-sm font-medium leading-5 text-white" title={meeting.title}>
            {meeting.title}
          </p>
          <p className="text-sm tabular-nums leading-5 text-white/70">
            <TimeAgo date={meeting.start} formatter={meetingCountdownFormatter} />
          </p>
        </div>
      </div>
      <div className={twMerge('flex flex-col', transcribesOnJoin && 'bg-white/ rounded-xl bg-white/10')}>
        {transcribesOnJoin && (
          <p className="w-full p-2 text-center text-sm font-medium leading-5 text-white/50">
            Transcription starts when you join
          </p>
        )}
        {meetingJoinLink ? (
          <Button
            variant="primary"
            tone="on-dark"
            className={NOTCH_MEETING_BUTTON_CLASS}
            data-notch-control="join meeting"
            onClick={(event) => {
              event.stopPropagation()
              onJoin()
            }}
          >
            {joinLabel}
          </Button>
        ) : (
          <Button
            variant="primary"
            tone="on-dark"
            className={NOTCH_MEETING_BUTTON_CLASS}
            data-notch-control="start transcribing"
            onClick={(event) => {
              event.stopPropagation()
              onStartTranscribing()
            }}
          >
            {transcribeLabel}
          </Button>
        )}
      </div>
    </div>
  )
}

interface NotchMeetingPrepareToastProps {
  meeting: CalendarEvent
  toastId: string
}

export function NotchMeetingPrepareToast({ meeting, toastId }: NotchMeetingPrepareToastProps) {
  const toast = useToast()
  const setSidepanelState = useSetSidepanelState()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()
  const { openView } = useViewActions()

  const onView = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      AmplitudeRenderer.trackEvent('Meeting Brief', {
        action: 'view',
        source: 'notch-prepare-toast',
        meeting_id: meeting.id,
      })
      trackSidepanelOpened('mini')
      setHotkeyOpened(true)
      setSidepanelState('open')
      openView(
        MEETING_VIEW_ID,
        { mode: 'note', calendarEventId: meeting.id, title: meeting.title, meeting },
        { sourceLabel: 'Home' },
      )
      toast.dismiss(toastId)
    },
    [meeting, openView, setHotkeyOpened, setSidepanelState, toast, toastId],
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2.5">
        <NotchToastIcon className="mt-0.5" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="line-clamp-2 break-words text-sm font-medium leading-5 text-white" title={meeting.title}>
            {meeting.title}
          </p>
          <p className="text-sm leading-5 text-white/70">Starts in ~10 minutes</p>
        </div>
      </div>
      <Button
        variant="primary"
        tone="on-dark"
        className={NOTCH_MEETING_BUTTON_CLASS}
        data-notch-control="view meeting"
        onClick={onView}
      >
        View
      </Button>
    </div>
  )
}
